// «Доступ к Claude» в настройках: работает ли подписка, когда истекает токен, замена токена.
// Токен вставляется один раз в защищённое поле; платформа проверяет его настоящим запросом, хранит
// зашифрованным и сама перезапускает агента. Обратно токен не показывается нигде — только последние символы.
import { Button, Card, Chip, toast, when } from '@autoaff/ui';
import { useRouterState } from '@tanstack/react-router';
import { KeyRound, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type AgentStatus, api } from '../api';
import { useAgentStatus } from '../streams';

function dateLong(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    timeZone: 'Asia/Tashkent',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function ClaudeAccessCard() {
  const { status } = useAgentStatus();
  const [formOpen, setFormOpen] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState<'check' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hash = useRouterState({ select: (s) => s.location.hash });
  const access = status?.access;
  const t = status?.claudeToken;
  const lost = access?.state === 'lost';

  // Пришли по ссылке из плашки «Обновить доступ» — форма сразу открыта (к карточке прокручивает страница)
  useEffect(() => {
    if (hash === 'claude-access') setFormOpen(true);
  }, [hash]);

  const check = async () => {
    setBusy('check');
    try {
      const s = await api<AgentStatus>('/api/claude/check', {});
      toast(s.access?.state === 'ok' ? 'Доступ к Claude работает' : (s.access?.message ?? 'Доступа нет'));
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await api('/api/claude/token', { token });
      setToken('');
      setFormOpen(false);
      toast('Новый токен принят — агент перезапустится, как только закончит текущую работу');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const state = !status?.runner
    ? { dot: 'off', title: 'Не проверить: сервис агента не подключён', text: null }
    : lost
      ? { dot: 'danger', title: 'Нет доступа', text: access?.message }
      : access?.state === 'ok'
        ? {
            dot: '',
            title: 'Работает',
            text: access.checkedAt ? `Проверено ${when(access.checkedAt)}` : null,
          }
        : { dot: 'warn', title: 'Ещё не проверялся', text: access?.message ?? null };

  return (
    <Card
      title="Доступ к Claude"
      action={
        status?.runner && (
          <Button size="sm" variant="ghost" icon={<RefreshCw />} onClick={check} disabled={busy !== null}>
            {busy === 'check' ? 'Проверяю…' : 'Проверить'}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="access-row">
          <span className={`status-dot ${state.dot}`} />
          <div>
            <b>{state.title}</b>
            {state.text && <p>{state.text}</p>}
          </div>
        </div>
        {t && (
          <div className="access-row">
            <KeyRound size={16} className="muted" style={{ marginTop: 3, flex: 'none' }} />
            <div>
              <b>
                Токен подписки <span className="mono muted">{t.hint}</span>
              </b>
              <p>
                {t.issuedAt ? `Выпущен ${dateLong(t.issuedAt)}` : 'Дата выпуска неизвестна'}
                {t.expiresAt ? ` · действует до ${dateLong(t.expiresAt)}` : ''}
              </p>
            </div>
            {t.daysLeft !== null && t.daysLeft <= 14 && (
              <Chip tone={t.daysLeft <= 3 ? 'danger' : 'warning'}>
                {t.daysLeft <= 0 ? 'истёк' : `осталось ${t.daysLeft} дн.`}
              </Chip>
            )}
          </div>
        )}
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          Платформа проверяет доступ каждые 12 минут крошечным запросом. Если он пропадёт — придёт плашка, а
          задачи агента подождут. О конце срока токена напомним за 2 недели и за 3 дня.
        </p>
      </div>

      {!formOpen ? (
        <div style={{ marginTop: 14 }}>
          <Button
            variant={lost ? 'primary' : 'default'}
            icon={<KeyRound />}
            onClick={() => setFormOpen(true)}
          >
            Обновить доступ к Claude
          </Button>
        </div>
      ) : (
        <form
          className="token-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (token.trim()) void save();
          }}
        >
          <ol>
            <li>
              На компьютере, где установлен Claude Code, выполните <code>claude setup-token</code> и войдите в
              свой аккаунт Claude.
            </li>
            <li>
              Скопируйте токен — он начинается с <code>sk-ant-oat</code> — и вставьте его ниже.
            </li>
          </ol>
          <label className="label" htmlFor="claude-token">
            Новый токен
          </label>
          <input
            id="claude-token"
            className="input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="sk-ant-oat01-…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'claude-token-error' : undefined}
          />
          {error && (
            <p id="claude-token-error" className="notice error" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          <div className="row">
            <Button type="submit" variant="primary" disabled={!token.trim() || busy !== null}>
              {busy === 'save' ? 'Проверяю токен…' : 'Проверить и сохранить'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setFormOpen(false);
                setToken('');
                setError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
